import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, ShieldCheck } from "lucide-react";

/* DUPR SSO (documented iframe flow). Security: only messages from the exact DUPR origin AND from our iframe's
   window are accepted; only userToken/refreshToken are forwarded once to the backend over HTTPS; the browser-
   supplied duprId is ignored (identity is verified server-side). Tokens are never kept in state or storage. */
export default function DuprConnectModal({ open, onClose, onLinked, completeUrl = "/dupr/link/complete" }) {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const frameRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    doneRef.current = false;
    setCfg(null); setErr("");
    api.get("/dupr/link/start").then(({ data }) => setCfg(data)).catch((e) => setErr(formatApiError(e?.response?.data?.detail)));
  }, [open]);

  useEffect(() => {
    if (!open || !cfg) return;
    const handler = async (event) => {
      if (event.origin !== cfg.origin) return;
      if (frameRef.current && event.source && event.source !== frameRef.current.contentWindow) return;
      let d = event.data;
      if (typeof d === "string") { try { d = JSON.parse(d); } catch { return; } }
      if (!d || typeof d !== "object" || Array.isArray(d)) return;
      // Documented payload: { userToken, refreshToken, id, duprId, stats } — enforce types, ignore anything else
      if (typeof d.userToken !== "string" || d.userToken.length < 20) return;
      if (d.refreshToken !== undefined && typeof d.refreshToken !== "string") return;
      if (d.id !== undefined && typeof d.id !== "number") return;
      if (d.duprId !== undefined && !(typeof d.duprId === "string" && /^[A-Za-z0-9]{4,8}$/.test(d.duprId))) return;
      if (d.stats !== undefined && (typeof d.stats !== "object" || d.stats === null)) return;
      if (doneRef.current) return;
      doneRef.current = true;
      setBusy(true);
      try {
        const { data } = await api.post(completeUrl, {
          user_token: d.userToken,
          refresh_token: typeof d.refreshToken === "string" ? d.refreshToken : null,
          claimed_dupr_id: typeof d.duprId === "string" ? d.duprId : null,
        });
        toast.success(`DUPR ${data.link?.dupr_id} connected`);
        onLinked?.(data.link);
        onClose?.();
      } catch (e) {
        doneRef.current = false;
        setErr(formatApiError(e?.response?.data?.detail));
      } finally {
        d = null; setBusy(false);
      }
    };
    window.addEventListener("message", handler, false);
    return () => window.removeEventListener("message", handler, false);
  }, [open, cfg, completeUrl, onLinked, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="dupr-connect-modal">
      <div className="relative w-full max-w-[520px] max-h-[94vh] overflow-y-auto bg-pb-surface border border-pb-border shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-pb-border/60">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-pb-cyan"><ShieldCheck className="w-4 h-4" /> Connect your DUPR account
            {cfg && cfg.env !== "production" && <span className="text-[10px] text-amber-400 border border-amber-400/40 px-1.5">{cfg.env} sandbox</span>}</div>
          <button data-testid="dupr-connect-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="px-4 pt-3 text-[11px] text-zinc-400">You are signing in on DUPR's own page. Pickle Bullpen never sees your DUPR password. DUPR will ask which details you consent to share; ratings and your DUPR ID are required.</p>
        <div className="p-4">
          {err && <div className="text-xs text-red-400 border border-red-500/40 bg-red-500/10 p-2 mb-3" data-testid="dupr-connect-error">{err}</div>}
          {!cfg && !err && <div className="flex items-center gap-2 text-xs text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Preparing secure DUPR sign-in…</div>}
          {cfg && (
            <iframe ref={frameRef} title="DUPR sign in" src={cfg.iframe_url} allow="payment" data-testid="dupr-sso-iframe"
              className="w-full h-[560px] bg-white border border-pb-border" />
          )}
          {busy && <div className="mt-2 flex items-center gap-2 text-xs text-pb-cyan" data-testid="dupr-connect-verifying"><Loader2 className="w-4 h-4 animate-spin" /> Verifying your DUPR identity…</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
